import type { ChannelType } from '@/features/channels/api';
import { apiRequest } from '@/shared/lib/api-client';

export type PushEvent =
  | 'message.received'
  | 'message.sent'
  | 'ticket.created'
  | 'ticket.closed'
  | 'message.status'
  | 'message.replied'
  | 'group.member_joined'
  | 'group.member_left';

export interface PushEndpoint {
  id: number;
  name: string;
  whatsapp_number_id: number;
  channel_name: string | null;
  channel_number: string | null;
  channel_type: ChannelType | null;
  platform: 'n8n' | 'fluent' | 'custom';
  url: string;
  events: PushEvent[];
  /** Campos adicionais fixos (chave→valor) enviados no `custom` de cada delivery. */
  custom_fields: Record<string, string>;
  active: boolean;
  created_at: string;
  updated_at: string;
}

export interface CreateEndpointInput {
  name: string;
  whatsapp_number_id: number;
  url: string;
  events: PushEvent[];
  custom_fields?: Record<string, string>;
}

export interface UpdateEndpointInput {
  name?: string;
  whatsapp_number_id?: number;
  url?: string;
  events?: PushEvent[];
  custom_fields?: Record<string, string>;
  active?: boolean;
}

export interface DeliveryRow {
  id: number;
  event_type: string;
  http_status: number | null;
  attempts: number;
  response_body: string | null;
  payload: unknown;
  delivered_at: string | null;
  failed_at: string | null;
  created_at: string;
}

export interface BucketStats {
  total: number;
  success: number;
  failed: number;
}

export function listEndpoints(
  filters: { limit?: number; cursor?: number } = {},
): Promise<{ items: PushEndpoint[]; next_cursor: number | null }> {
  const params = new URLSearchParams();
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  const qs = params.toString();
  return apiRequest(`/push-endpoints${qs ? `?${qs}` : ''}`);
}
export function getEndpoint(id: number): Promise<PushEndpoint> {
  return apiRequest(`/push-endpoints/${id}`);
}
export function createEndpoint(
  input: CreateEndpointInput,
): Promise<{ id: number; secret: string }> {
  return apiRequest('/push-endpoints', { method: 'POST', body: input });
}
export function updateEndpoint(id: number, input: UpdateEndpointInput): Promise<void> {
  return apiRequest(`/push-endpoints/${id}`, { method: 'PATCH', body: input });
}
export function deleteEndpoint(id: number): Promise<void> {
  return apiRequest(`/push-endpoints/${id}`, { method: 'DELETE' });
}
export function listDeliveries(
  id: number,
  filters: {
    event_type?: string;
    status?: 'success' | 'failed' | 'pending';
    limit?: number;
    cursor?: number;
  } = {},
): Promise<{ items: DeliveryRow[]; next_cursor: number | null }> {
  const params = new URLSearchParams();
  if (filters.event_type) params.set('event_type', filters.event_type);
  if (filters.status) params.set('status', filters.status);
  if (filters.limit) params.set('limit', String(filters.limit));
  if (filters.cursor) params.set('cursor', String(filters.cursor));
  const qs = params.toString();
  return apiRequest(`/push-endpoints/${id}/deliveries${qs ? `?${qs}` : ''}`);
}
export function getStats(id: number): Promise<Record<string, BucketStats>> {
  return apiRequest(`/push-endpoints/${id}/stats`);
}

export const ALLOWED_EVENTS: PushEvent[] = [
  'ticket.closed',
  'ticket.created',
  'message.received',
  'message.sent',
  'message.status',
  'message.replied',
  'group.member_joined',
  'group.member_left',
];

/**
 * Disponibilidade de cada evento (Fase 1): `ticket.closed` dispara em QR +
 * Cloud API. Os genéricos (ticket.created, message.received, message.sent)
 * ficam desativados em TODOS os canais até a Fase 2 cobri-los de forma
 * consistente nos dois canais.
 *
 * `message.status` / `message.replied` são o funil de DISPARO EXTERNO
 * (POST /external/messages com correlation_id): entregue/lido/falha + primeira
 * resposta do destinatário. NÃO espelham a caixa de entrada — só mensagens
 * disparadas com correlation_id emitem (Cloud API).
 */
export function isEventAvailable(event: PushEvent): boolean {
  return (
    event === 'ticket.closed' ||
    event === 'message.status' ||
    event === 'message.replied' ||
    event === 'group.member_joined' ||
    event === 'group.member_left'
  );
}

export function eventNote(event: PushEvent): string | null {
  if (event === 'message.status') return 'funil de disparo externo (entregue/lido/falha)';
  if (event === 'message.replied') return 'funil de disparo externo (1ª resposta)';
  if (event === 'group.member_joined') return 'membro entrou em grupo';
  if (event === 'group.member_left') return 'membro saiu de grupo';
  return event === 'ticket.closed' ? null : 'em breve';
}
